import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  AddIcon,
  DeleteIcon,
  EditIcon,
  useClientInfo
} from "../../utils/commonImports";

import BunkFormAdd from "./BunkFormAdd.jsx";

const API = "/api/bunks";

export default function BunkForm({ initialData = [] }) {
  const { clientId } = useClientInfo();

  const tableRef = useRef();
  const [rows, setRows] = useState(initialData);
  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState(null);

  const [anchorEl, setAnchorEl] = useState(null);
  const [menuRow, setMenuRow] = useState(null);

  const fetchRows = useCallback(async () => {
    if (!clientId) return;

    try {
      const res = await fetch(`${API}?client_id=${clientId}`);
      const json = await res.json();
      setRows(Array.isArray(json) ? json : json.data || []);
    } catch (err) {
      console.error("Failed to fetch bunks", err);
      setRows([]);
    }
  }, [clientId]);

  useEffect(() => {
    if (!showForm) {
      fetchRows();
    }
  }, [showForm, fetchRows]);

  const handleAddClick = () => {
    setEditData(null);
    setShowForm(true);
  };

  const handleEditClick = (row) => {
    setEditData(row);
    setShowForm(true);
    handleMenuClose();
  };

  const handleDeleteClick = async (row) => {
    handleMenuClose();
    if (!window.confirm(`Are you sure you want to delete bunk: ${row.bunk_name}?`))
      return;
    try {
      await fetch(`${API}/${row.bunkid}?client_id=${clientId}`, { method: "DELETE" });
      fetchRows();
    } catch (err) {
      console.error("Delete bunk failed", err);
    }
  };

  const handleMenuOpen = (event, row) => {
    setAnchorEl(event.currentTarget);
    setMenuRow(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuRow(null);
  };

  return (
    <Box>
      {!showForm ? (
        <>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6">Bunk List</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddClick}
              size="small"
            >
              Add Bunk
            </Button>
          </Box>

          <Paper ref={tableRef} sx={{ width: "100%", overflowX: "auto" }}>
            <Table size="small" sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Bunk Name</TableCell>
                  <TableCell>Bunk Address</TableCell>
                  <TableCell>Bunk Phone</TableCell>
                  <TableCell>Contact Person</TableCell>
                  <TableCell>Contact Mobile</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Fuel Surcharge</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                      No bunks to display.
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <TableRow key={row.bunkid} hover>
                      <TableCell>{row.bunk_name}</TableCell>
                      <TableCell>{row.bunk_address}</TableCell>
                      <TableCell>{row.bunk_phone}</TableCell>
                      <TableCell>{row.bunk_contact_person}</TableCell>
                      <TableCell>{row.bunk_contact_person_mobile}</TableCell>
                      <TableCell>{row.bunk_email}</TableCell>
                      <TableCell>{row.bunk_fuel_surcharge}</TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, row)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteClick(row)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Paper>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <MenuItem
              onClick={() => {
                if (menuRow) {
                  handleEditClick(menuRow);
                }
              }}
            >
              Edit
            </MenuItem>
            <MenuItem
              onClick={() => {
                if (menuRow) {
                  handleDeleteClick(menuRow);
                }
              }}
            >
              Delete
            </MenuItem>
          </Menu>
        </>
      ) : (
        <BunkFormAdd
          editData={editData}
          onCancel={() => setShowForm(false)}
          onSuccess={() => {
            setShowForm(false);
            fetchRows();
          }}
        />
      )}
    </Box>
  );
}
